import {
  basistheory,
  type BasisTheoryElements,
  type BasisTheoryInitOptions,
} from '@basis-theory/web-elements';

export const BASIS_THEORY_INITIALIZATION_ATTEMPT_TIMEOUT_MS = 10_000;
const MAX_INITIALIZATION_ATTEMPTS = 2;

type BasisTheoryDataElement = {
  unmount(): void;
  iframe?: HTMLIFrameElement;
};

type BasisTheoryWindow = Window & {
  DataElement?: BasisTheoryDataElement;
};

type InitializationConfig = {
  publicKey: string;
  options: BasisTheoryInitOptions;
};

type PendingInitialization = {
  kind: 'pending';
  key: string;
  waiters: Set<symbol>;
  promise: Promise<BasisTheoryElements>;
  restartableAfterFailure: boolean;
};

type ReadyInitialization = {
  kind: 'ready';
  key: string;
  provider: BasisTheoryElements;
};

type FailedInitialization = {
  kind: 'failed';
  key: string;
  error: Error;
  restartable: boolean;
};

type InitializationState =
  | PendingInitialization
  | ReadyInitialization
  | FailedInitialization;

const stateByWindow = new WeakMap<object, InitializationState>();

class InitializationAttemptTimeoutError extends Error {
  constructor() {
    super('Basis Theory Elements initialization timed out.');
    this.name = 'InitializationAttemptTimeoutError';
  }
}

const initializationCancelledError = (): Error =>
  new Error('Basis Theory Elements initialization was cancelled.');

const conflictingConfigurationError = (): Error =>
  new Error('Basis Theory Elements is already initialized with another configuration.');

const configurationKey = (config: InitializationConfig): string =>
  JSON.stringify({ publicKey: config.publicKey, options: config.options });

const currentDataElement = (owner: BasisTheoryWindow): BasisTheoryDataElement | undefined =>
  owner.DataElement;

const cleanupDataElement = (
  owner: BasisTheoryWindow,
  candidate: BasisTheoryDataElement | undefined,
): boolean => {
  if (!candidate || currentDataElement(owner) !== candidate) return false;
  const iframe = candidate.iframe;
  try {
    candidate.unmount();
    return true;
  } catch {
    // Basis Theory can reject unmount while its own mount promise is pending.
    // The fallback removes only the iframe owned by this exact captured object.
    if (!iframe) return false;
    iframe.remove();
    return true;
  }
};

const withDeadline = async <T>(promise: Promise<T>): Promise<T> => {
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = globalThis.setTimeout(
      () => reject(new InitializationAttemptTimeoutError()),
      BASIS_THEORY_INITIALIZATION_ATTEMPT_TIMEOUT_MS,
    );
  });
  // A provider promise can settle after our deadline. Keep that late rejection
  // handled; generation/state publication remains owned by the outer run.
  void promise.catch(() => undefined);
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
  }
};

const initializeRun = async (
  owner: BasisTheoryWindow,
  config: InitializationConfig,
  run: PendingInitialization,
): Promise<BasisTheoryElements> => {
  let lastError: unknown;

  for (let attempt = 0; attempt < MAX_INITIALIZATION_ATTEMPTS; attempt += 1) {
    if (run.waiters.size === 0) throw initializationCancelledError();
    const previousDataElement = currentDataElement(owner);
    const initialization = Promise.resolve().then(() =>
      basistheory(config.publicKey, config.options),
    );
    const attemptState: {
      candidate?: BasisTheoryDataElement;
      cleanup?: {
        candidate: BasisTheoryDataElement;
        succeeded: boolean;
      };
      discardLateResult: boolean;
    } = { discardLateResult: false };
    const cleanupAttemptCandidate = (candidate: BasisTheoryDataElement): boolean => {
      if (attemptState.cleanup?.candidate === candidate) {
        return attemptState.cleanup.succeeded;
      }
      const succeeded = cleanupDataElement(owner, candidate);
      attemptState.cleanup = { candidate, succeeded };
      return succeeded;
    };
    const cleanupLateResult = (): void => {
      if (!attemptState.discardLateResult) return;
      const candidate = attemptState.candidate ?? currentDataElement(owner);
      if (candidate && candidate !== previousDataElement) {
        cleanupAttemptCandidate(candidate);
      }
    };
    void initialization.then(cleanupLateResult, cleanupLateResult);

    try {
      const provider = await withDeadline(initialization);
      if (!provider) throw new Error('Basis Theory Elements did not initialize.');
      if (run.waiters.size === 0) throw initializationCancelledError();
      return provider;
    } catch (error) {
      lastError = error;
      const candidate = currentDataElement(owner);
      const ownsCandidate = candidate !== undefined && candidate !== previousDataElement;
      if (ownsCandidate) attemptState.candidate = candidate;
      const cleaned = ownsCandidate && cleanupAttemptCandidate(candidate);
      attemptState.discardLateResult = error instanceof InitializationAttemptTimeoutError;

      const canRetry =
        attempt + 1 < MAX_INITIALIZATION_ATTEMPTS &&
        run.waiters.size > 0 &&
        cleaned;
      if (!canRetry) {
        run.restartableAfterFailure = cleaned;
        throw error;
      }
    }
  }

  throw lastError;
};

const observePendingInitialization = (
  run: PendingInitialization,
  signal: AbortSignal,
): Promise<BasisTheoryElements> => {
  if (signal.aborted) return Promise.reject(initializationCancelledError());
  const waiter = Symbol('basis-theory-initialization-waiter');
  run.waiters.add(waiter);

  return new Promise<BasisTheoryElements>((resolve, reject) => {
    let settled = false;
    const finish = (callback: () => void): void => {
      if (settled) return;
      settled = true;
      run.waiters.delete(waiter);
      signal.removeEventListener('abort', handleAbort);
      callback();
    };
    const handleAbort = () => finish(() => reject(initializationCancelledError()));
    signal.addEventListener('abort', handleAbort, { once: true });
    void run.promise.then(
      (provider) => finish(() => resolve(provider)),
      (error: unknown) =>
        finish(() => reject(error instanceof Error ? error : new Error(String(error)))),
    );
  });
};

/**
 * Returns the one Basis Theory v2 client owned by the current document.
 *
 * Basis Theory v2.12.1 uses a mutable DataElement singleton. This coordinator
 * prevents three split fields (or multiple Elements groups) from racing
 * independent initialization calls, and permits one recovery only when the
 * failed attempt created an exact DataElement instance that can be fenced and
 * cleaned safely.
 */
export const initializeBasisTheoryV2 = (
  config: InitializationConfig,
  signal: AbortSignal,
): Promise<BasisTheoryElements> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Basis Theory Elements requires a browser document.'));
  }
  if (signal.aborted) return Promise.reject(initializationCancelledError());

  const owner = window as BasisTheoryWindow;
  const key = configurationKey(config);
  const existing = stateByWindow.get(owner);
  if (existing) {
    if (existing.key !== key) return Promise.reject(conflictingConfigurationError());
    if (existing.kind === 'ready') return Promise.resolve(existing.provider);
    if (existing.kind === 'failed') {
      if (!existing.restartable) return Promise.reject(existing.error);
      stateByWindow.delete(owner);
      return initializeBasisTheoryV2(config, signal);
    }
    return observePendingInitialization(existing, signal);
  }

  const run = {
    kind: 'pending',
    key,
    waiters: new Set<symbol>(),
    restartableAfterFailure: false,
  } as PendingInitialization;
  // Add the first waiter before initialization starts so an immediately
  // executing run has a live owner.
  const firstWaiter = Symbol('basis-theory-initialization-first-waiter');
  run.waiters.add(firstWaiter);
  run.promise = initializeRun(owner, config, run);
  run.waiters.delete(firstWaiter);
  stateByWindow.set(owner, run);

  void run.promise.then(
    (provider) => {
      if (stateByWindow.get(owner) === run) {
        stateByWindow.set(owner, { kind: 'ready', key, provider });
      }
    },
    (error: unknown) => {
      if (stateByWindow.get(owner) === run) {
        stateByWindow.set(owner, {
          kind: 'failed',
          key,
          error: error instanceof Error ? error : new Error(String(error)),
          restartable: run.restartableAfterFailure,
        });
      }
    },
  );

  return observePendingInitialization(run, signal);
};
